import { useQuery } from '@tanstack/react-query';

import { getApprovalDocList } from '@entities/approval/api/approval';
import { formatDateToYYYYMMDDHHmmss } from '@shared/utils/formatDate';

/**
 * 관리자 문서 목록 조회
 * - 검색 버튼 클릭 시 또는 필터 옵션 로드 후 적용된 검색 조건으로 조회
 * @param {Object|null} searchParams - 적용된 검색 조건 (null이면 미조회)
 * @param {Object} filterOptions - { teams, docTypes } 팀/양식 옵션 (teamCode, formCode 매핑용)
 * @returns {UseQueryResult<Array>}
 */
function useAdminDocumentsQuery(searchParams, filterOptions) {
  const teams = filterOptions?.teams ?? [];
  const docTypes = filterOptions?.docTypes ?? [];

  return useQuery({
    queryKey: ['adminDocuments', searchParams, teams, docTypes],
    queryFn: async () => {
      const params = {
        type: 'all',
        date1: searchParams.queryPeriod_start,
        date2: searchParams.queryPeriod_end,
        title: searchParams.documentTitle,
        name: searchParams.drafter,
        stateCode: searchParams.documentStatus !== '' ? searchParams.documentStatus : undefined,
        teamCode:
          searchParams.draftDepartment && searchParams.draftDepartment !== '전체'
            ? teams.find((t) => t.teamName === searchParams.draftDepartment)?.code
            : undefined,
        formCode:
          searchParams.documentType && searchParams.documentType !== '전체'
            ? docTypes.find((d) => d.formName === searchParams.documentType)?.code
            : undefined,
      };

      const response = await getApprovalDocList(params);

      if (response.result_code !== 0) {
        return [];
      }

      const list = response.resultData?.docInfoList ?? [];
      const seen = new Set();
      const deduped = [];

      for (const item of list) {
        if (item.idx && seen.has(item.idx)) continue;
        if (item.idx) seen.add(item.idx);
        const isCorpSealUsed =
          item.is_corp_seal_used === 1 ||
          item.is_corp_seal_used === '1' ||
          item.corpSealUsed === true;
        deduped.push({
          id: item.idx,
          documentType: item.docType || '-',
          documentNumber: item.docNumber || '-',
          documentTitle: item.title || '-',
          draftDate: formatDateToYYYYMMDDHHmmss(item.date),
          draftDepartment: item.team || '-',
          drafter: item.name || '-',
          documentStatus: item.stateCode === 5 ? '삭제' : item.state || '-',
          stateCode: item.stateCode,
          is_corp_seal_used: isCorpSealUsed ? 1 : 0,
          corpSealUsed: isCorpSealUsed ? 'Y' : 'N',
        });
      }

      return deduped;
    },
    enabled:
      searchParams !== null &&
      typeof searchParams === 'object' &&
      searchParams.queryPeriod_start != null &&
      searchParams.queryPeriod_end != null,
  });
}

export default useAdminDocumentsQuery;
